<template>
  <div class="workspace-permissoes-page">
    <!-- Verificação de permissão -->
    <div v-if="!isCheckingPermission && !hasPermission" class="no-permission-message">
      <div class="no-permission-card">
        <svg class="permission-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
        </svg>
        <h2>{{ t('permissoes.accessDeniedTitle') }}</h2>
        <p>{{ t('permissoes.accessDeniedMessage') }}</p>
        <button @click="$router.push('/dashboard')" class="btn-primary">
          {{ t('permissoes.backToDashboard') }}
        </button>
      </div>
    </div>

    <!-- Conteúdo da página (apenas para admins) -->
    <template v-else-if="hasPermission">
      <!-- Header moderno -->
      <div class="modern-header">
        <div class="header-container">
          <!-- Breadcrumb e navegação -->
          <div class="breadcrumb-section">
            <nav class="breadcrumb">
              <button @click="$router.push('/dashboard')" class="breadcrumb-item">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                </svg>
                {{ t('common.breadcrumb.dashboard') }}
              </button>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">{{ t('permissoes.breadcrumbCurrent') }}</span>
            </nav>
          </div>

          <!-- Título e informações principais -->
          <div class="title-section">
            <div class="title-content">
              <div class="title-icon">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <div class="title-text">
                <h1 class="main-title">{{ t('permissoes.title') }}</h1>
                <p class="subtitle">{{ t('permissoes.subtitle') }}</p>
              </div>
            </div>
            
            <!-- Badge de Dashboard -->
            <div class="workspace-badge type-dashboard">
              <div class="badge-icon">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
              </div>
              <span class="badge-text">{{ t('permissoes.badgeDashboard') }}</span>
            </div>
          </div>

          <!-- Ações do header -->
          <div class="actions-section">
            <!-- Toggle de Visualização -->
            <div class="view-toggles">
              <!-- Toggle Por Workspace -->
              <div class="tooltip-container">
                <button 
                  @click="currentView = 'by-workspace'"
                  class="toggle-btn"
                  :class="{ 'active': currentView === 'by-workspace' }"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                  </svg>
                </button>
                <div class="tooltip">{{ t('permissoes.viewByWorkspace') }}</div>
              </div>
              
              <!-- Toggle Por Usuário -->
              <div class="tooltip-container">
                <button 
                  @click="currentView = 'by-user'"
                  class="toggle-btn"
                  :class="{ 'active': currentView === 'by-user' }"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                </button>
                <div class="tooltip">{{ t('permissoes.viewByUser') }}</div>
              </div>

              <!-- Toggle Por Telas -->
              <div class="tooltip-container">
                <button 
                  @click="currentView = 'by-screens'"
                  class="toggle-btn"
                  :class="{ 'active': currentView === 'by-screens' }"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </button>
                <div class="tooltip">{{ t('permissoes.viewByScreens') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Conteúdo principal -->
      <div class="main-content">

      <!-- Loading -->
      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p style="color: var(--text-secondary)">{{ t('permissoes.loadingData') }}</p>
      </div>

        <!-- Visualização por Workspace -->
      <div v-else-if="currentView === 'by-workspace'" class="view-content">
        <div class="column-wrapper">
          <h3 class="column-title">{{ t('permissoes.columnWorkspaces') }}</h3>
          <div class="workspaces-grid">
            <div 
              v-for="workspace in workspaces" 
              :key="workspace.id"
              @click="selectWorkspace(workspace)"
              class="workspace-card"
              :class="{ selected: selectedWorkspace?.id === workspace.id }"
            >
              <!-- Thumbnail/Cover do workspace -->
              <div class="workspace-cover" :style="{ backgroundColor: workspace.corWorkspace || '#5865F2' }">
                <img 
                  v-if="workspace.fotoWorkspace" 
                  :src="workspace.fotoWorkspace" 
                  :alt="workspace.nome"
                  class="workspace-cover-img"
                />
                <div v-else class="workspace-cover-placeholder">
                  <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                </div>
              </div>

              <!-- Card body -->
              <div class="workspace-body">
                <div class="workspace-header">
                  <div class="workspace-icon-badge" :style="{ backgroundColor: workspace.corWorkspace || '#5865F2' }">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                    </svg>
                  </div>
                  <div class="workspace-info">
                    <h3 class="workspace-name">{{ workspace.nome }}</h3>
                    <span class="workspace-type-badge">{{ workspace.tipo }}</span>
                  </div>
                </div>

                <div class="workspace-footer">
                  <div class="workspace-users-section">
                    <div class="workspace-users-avatars">
                      <div 
                        v-for="(acesso, index) in getWorkspaceUsers(workspace.id).slice(0, 4)" 
                        :key="acesso.id"
                        class="user-avatar-mini"
                        :style="{ zIndex: 10 - index, marginLeft: index > 0 ? '-8px' : '0' }"
                        :title="acesso.usuarioNome"
                      >
                        <img v-if="acesso.usuarioFotoPerfil" :src="acesso.usuarioFotoPerfil" :alt="acesso.usuarioNome" />
                        <div v-else class="avatar-placeholder-mini">
                          {{ getInitials(acesso.usuarioNome) }}
                        </div>
                      </div>
                      <div 
                        v-if="(workspace.totalUsuarios || 0) > 4" 
                        class="user-avatar-mini more-users"
                        style="margin-left: -8px"
                        :title="t('permissoes.moreUsers', { count: (workspace.totalUsuarios || 0) - 4 })"
                      >
                        +{{ (workspace.totalUsuarios || 0) - 4 }}
                      </div>
                    </div>
                  </div>
                  <div class="workspace-stat-badge">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                    </svg>
                    <span>{{ workspace.totalUsuarios || 0 }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Detalhes do workspace selecionado -->
        <div class="column-wrapper">
          <div class="details-header">
            <h3 class="column-title">{{ selectedWorkspace ? t('permissoes.usersInWorkspace', { name: selectedWorkspace.nome }) : t('permissoes.selectWorkspace') }}</h3>
            <button v-if="selectedWorkspace" @click="showInviteModal = true" class="btn-primary btn-sm">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              {{ t('permissoes.invite') }}
            </button>
          </div>

          <div v-if="selectedWorkspace" class="workspace-details">
            <div class="users-list">
              <div 
                v-for="acesso in workspaceUsuarios" 
                :key="acesso.id"
                class="user-item"
              >
                <div class="user-avatar">
                  <img v-if="acesso.usuarioFotoPerfil" :src="acesso.usuarioFotoPerfil" :alt="acesso.usuarioNome" />
                  <div v-else class="avatar-placeholder">
                    {{ getInitials(acesso.usuarioNome) }}
                  </div>
                </div>
                <div class="user-info">
                  <h4>{{ acesso.usuarioNome }}</h4>
                  <p>{{ acesso.usuarioEmail }}</p>
                </div>
                <div class="user-role">
                  <WorkspaceRoleSelect
                    v-if="acesso.roleWorkspace !== 'OWNER'"
                    v-model="acesso.roleWorkspace"
                    :workspace-type="selectedWorkspace?.tipo || 'TAREFAS'"
                    :show-owner-option="false"
                    size="compact"
                    @change="updateUserPermission(acesso)"
                  />
                  <span v-else class="perm-owner-badge">{{ t('permissoes.roleOwner') }}</span>
                </div>
                <button 
                  v-if="acesso.roleWorkspace !== 'OWNER'"
                  @click="removeUserFromWorkspace(acesso)"
                  class="btn-danger-icon"
                  :title="t('permissoes.removeUserTitle')"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <p style="color: var(--text-secondary)">{{ t('permissoes.selectWorkspaceUsers') }}</p>
          </div>
        </div>
      </div>

        <!-- Visualização por Usuário -->
      <div v-else-if="currentView === 'by-user'" class="view-content">
        <div class="column-wrapper">
          <h3 class="column-title">{{ t('permissoes.columnUsers') }}</h3>
          <div class="users-grid">
            <div 
              v-for="usuario in usuarios" 
              :key="usuario.id"
              @click="selectUser(usuario)"
              class="user-card"
              :class="{ selected: selectedUser?.id === usuario.id }"
            >
              <!-- Avatar grande centralizado -->
              <div class="user-card-header">
                <div class="user-avatar-large">
                  <img v-if="usuario.fotoPerfil" :src="usuario.fotoPerfil" :alt="usuario.nomeCompleto" />
                  <div v-else class="avatar-placeholder-large">
                    {{ getInitials(usuario.nomeCompleto) }}
                  </div>
                </div>
              </div>

              <!-- Card body -->
              <div class="user-card-body">
                <div class="user-card-info">
                  <h3 class="user-name">{{ usuario.nomeCompleto }}</h3>
                  <p class="user-email">{{ usuario.email }}</p>
                </div>

                <div class="user-card-footer">
                  <span class="user-role-badge" :class="`role-${usuario.role?.nome.toLowerCase()}`">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                    </svg>
                    {{ usuario.role?.nome }}
                  </span>
                  <div class="user-workspaces-count">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                    </svg>
                    <span>{{ getUserWorkspaceCount(usuario.id) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Workspaces do usuário selecionado -->
        <div class="column-wrapper">
          <div class="details-header">
            <h3 class="column-title">{{ selectedUser ? t('permissoes.workspacesOf', { name: selectedUser.nomeCompleto }) : t('permissoes.selectUser') }}</h3>
            <button v-if="selectedUser" @click="showAddWorkspaceModal = true" class="btn-primary btn-sm">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              {{ t('permissoes.add') }}
            </button>
          </div>

          <div v-if="selectedUser" class="user-workspaces-details">
            <div class="user-workspaces-grid">
              <div 
                v-for="acesso in userWorkspaces" 
                :key="acesso.id"
                class="workspace-card-small"
              >
                <!-- Cover do workspace -->
                <div class="workspace-small-cover" :style="{ backgroundColor: acesso.workspaceCorWorkspace || '#5865F2' }">
                  <img 
                    v-if="acesso.workspaceFotoWorkspace" 
                    :src="acesso.workspaceFotoWorkspace" 
                    :alt="acesso.workspaceNome"
                    class="workspace-small-cover-img"
                  />
                  <div v-else class="workspace-small-cover-placeholder">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                    </svg>
                  </div>
                </div>

                <!-- Card body -->
                <div class="workspace-small-body">
                  <div class="workspace-small-header">
                    <div class="workspace-icon-badge-small" :style="{ backgroundColor: acesso.workspaceCorWorkspace || '#5865F2' }">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                      </svg>
                    </div>
                    <div class="workspace-small-info">
                      <h4 class="workspace-small-name">{{ acesso.workspaceNome }}</h4>
                      <span class="workspace-small-type">{{ acesso.workspaceTipo }}</span>
                    </div>
                  </div>

                  <div class="workspace-small-footer">
                    <WorkspaceRoleSelect
                      v-if="acesso.roleWorkspace !== 'OWNER'"
                      v-model="acesso.roleWorkspace"
                      :workspace-type="acesso.workspaceTipo || 'TAREFAS'"
                      :show-owner-option="false"
                      size="compact"
                      @change="updateWorkspacePermission(acesso)"
                    />
                    <span v-else class="perm-owner-badge perm-owner-badge--small">{{ t('permissoes.roleOwner') }}</span>
                    <button 
                      v-if="acesso.roleWorkspace !== 'OWNER'"
                      @click="removeWorkspaceFromUser(acesso)"
                      class="btn-delete-small"
                      :title="t('permissoes.removeAccessTitle')"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <p style="color: var(--text-secondary)">{{ t('permissoes.selectUserWorkspaces') }}</p>
          </div>
        </div>
      </div>

      <!-- Visualização por Telas (Acesso às Telas) -->
      <div v-else-if="currentView === 'by-screens'" class="view-content">
        <div class="column-wrapper">
          <h3 class="column-title">{{ t('permissoes.columnUsers') }}</h3>
          <div class="users-grid">
            <div 
              v-for="usuario in usuarios" 
              :key="'screen-' + usuario.id"
              @click="selectUserForScreens(usuario)"
              class="user-card"
              :class="{ selected: selectedScreenUser?.id === usuario.id }"
            >
              <div class="user-card-header">
                <div class="user-avatar-large">
                  <img v-if="usuario.fotoPerfil" :src="usuario.fotoPerfil" :alt="usuario.nomeCompleto" />
                  <div v-else class="avatar-placeholder-large">
                    {{ getInitials(usuario.nomeCompleto) }}
                  </div>
                </div>
              </div>
              <div class="user-card-body">
                <div class="user-card-info">
                  <h3 class="user-name">{{ usuario.nomeCompleto }}</h3>
                  <p class="user-email">{{ usuario.email }}</p>
                </div>
                <div class="user-card-footer">
                  <span class="user-role-badge" :class="`role-${usuario.role?.nome?.toLowerCase()}`">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                    </svg>
                    {{ usuario.role?.nome }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Permissões de tela do usuário selecionado -->
        <div class="column-wrapper">
          <div class="details-header">
            <h3 class="column-title">{{ selectedScreenUser ? t('permissoes.accessOf', { name: selectedScreenUser.nomeCompleto }) : t('permissoes.selectUser') }}</h3>
            <button 
              v-if="selectedScreenUser && screenPermissionsChanged" 
              @click="saveUserScreenPermissions" 
              class="btn-primary btn-sm"
              :disabled="savingScreenPermissions"
            >
              <svg v-if="!savingScreenPermissions" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              <div v-else class="btn-spinner"></div>
              {{ savingScreenPermissions ? t('permissoes.saving') : t('permissoes.saveChanges') }}
            </button>
          </div>

          <div v-if="loadingScreenPermissions" class="loading-container" style="min-height: 200px;">
            <div class="spinner"></div>
            <p style="color: var(--text-secondary)">{{ t('permissoes.loadingPermissions') }}</p>
          </div>

          <div v-else-if="selectedScreenUser" class="screen-permissions-panel">
            <!-- Aviso para admins -->
            <div v-if="isSelectedUserAdmin" class="admin-notice">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
              <div>
                <strong>{{ t('permissoes.adminNoticeTitle') }}</strong>
                <p>{{ t('permissoes.adminNoticeText') }}</p>
              </div>
            </div>

            <!-- Lista de telas com toggles -->
            <div class="screens-list">
              <div 
                v-for="screen in screensList" 
                :key="screen.id"
                class="screen-item"
                :class="{ 'screen-disabled': isSelectedUserAdmin }"
              >
                <div class="screen-info">
                  <div class="screen-icon" :style="{ background: screen.color }">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="screen.icon"/>
                    </svg>
                  </div>
                  <div class="screen-details">
                    <h4 class="screen-name">{{ screen.label }}</h4>
                    <p class="screen-description">{{ screen.description }}</p>
                  </div>
                </div>
                <div class="screen-toggle-wrapper">
                  <label class="screen-toggle" :class="{ 'toggle-disabled': isSelectedUserAdmin }">
                    <input 
                      type="checkbox"
                      :checked="getScreenAccess(screen.id)"
                      @change="toggleScreenAccess(screen.id)"
                      :disabled="isSelectedUserAdmin"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="toggle-label" :class="getScreenAccess(screen.id) ? 'access-granted' : 'access-denied'">
                    {{ getScreenAccess(screen.id) ? t('permissoes.granted') : t('permissoes.denied') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <p style="color: var(--text-secondary)">{{ t('permissoes.selectUserScreens') }}</p>
          </div>
        </div>
      </div>

      </div>
    </template>

    <!-- Modal: Convidar Usuário -->
    <div v-if="showInviteModal" class="modal-overlay" @click.self="showInviteModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ t('permissoes.inviteModalTitle', { name: selectedWorkspace?.nome }) }}</h3>
          <button @click="showInviteModal = false" class="modal-close">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <!-- Busca de usuários -->
          <div class="search-box">
            <svg class="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input 
              v-model="searchUser" 
              type="text" 
              :placeholder="t('permissoes.searchUser')"
              class="search-input"
            />
          </div>

          <!-- Lista de usuários -->
          <div class="users-select-list">
            <div 
              v-for="usuario in filteredUsuariosToInvite" 
              :key="usuario.id"
              @click="toggleUserSelection(usuario)"
              class="user-select-item"
              :class="{ 
                selected: isUserSelected(usuario.id),
                disabled: isUserInWorkspace(usuario.id)
              }"
            >
              <div class="selection-checkbox">
                <div v-if="isUserSelected(usuario.id)" class="checkbox-checked">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <div v-else class="checkbox-unchecked"></div>
              </div>
              <div class="user-select-avatar">
                <img v-if="usuario.fotoPerfil" :src="usuario.fotoPerfil" :alt="usuario.nomeCompleto" />
                <div v-else class="avatar-placeholder">
                  {{ getInitials(usuario.nomeCompleto) }}
                </div>
              </div>
              <div class="user-select-info">
                <h4>{{ usuario.nomeCompleto }}</h4>
                <p>{{ usuario.email }}</p>
              </div>
              <div v-if="isUserInWorkspace(usuario.id)" class="badge-already-member">
                {{ t('permissoes.alreadyMember') }}
              </div>
            </div>
          </div>

          <!-- Contador de seleção -->
          <div v-if="selectedUsersToInvite.length > 0" class="selection-counter">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
            <span>{{ selectedUsersToInvite.length }} {{ selectedUsersToInvite.length === 1 ? t('permissoes.usersSelectedOne') : t('permissoes.usersSelectedMany') }}</span>
            <button @click="clearSelection" class="clear-selection-btn">
              {{ t('permissoes.clear') }}
            </button>
          </div>

          <!-- Info sobre permissão -->
          <div class="permission-info">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span>{{ t('permissoes.inviteMemberHint') }} <strong>{{ t('permissoes.inviteMemberRole') }}</strong></span>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="showInviteModal = false" class="btn-secondary">
            {{ t('common.cancel') }}
          </button>
          <button 
            @click="inviteUsersToWorkspace" 
            class="btn-primary"
            :disabled="selectedUsersToInvite.length === 0"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            {{ selectedUsersToInvite.length > 0 ? t('permissoes.addUsersCount', { count: selectedUsersToInvite.length }) : t('permissoes.addUsers') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para adicionar workspaces ao usuário -->
    <div v-if="showAddWorkspaceModal" class="modal-overlay" @click.self="showAddWorkspaceModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ t('permissoes.addWorkspacesModalTitle', { name: selectedUser?.nomeCompleto }) }}</h3>
          <button @click="showAddWorkspaceModal = false" class="modal-close">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <!-- Busca de workspaces -->
          <div class="search-box">
            <svg class="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input 
              v-model="searchWorkspace" 
              type="text" 
              :placeholder="t('permissoes.searchWorkspace')"
              class="search-input"
            />
          </div>

          <!-- Lista de workspaces -->
          <div class="users-select-list">
            <div 
              v-for="workspace in filteredWorkspacesToAdd" 
              :key="workspace.id"
              @click="toggleWorkspaceSelection(workspace)"
              class="user-select-item"
              :class="{ 
                selected: isWorkspaceSelected(workspace.id),
                disabled: isWorkspaceAssignedToUser(workspace.id)
              }"
            >
              <div class="selection-checkbox">
                <div v-if="isWorkspaceSelected(workspace.id)" class="checkbox-checked">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <div v-else class="checkbox-unchecked"></div>
              </div>
              <div class="workspace-select-avatar" :style="{ backgroundColor: workspace.corWorkspace || '#5865F2' }">
                <img v-if="workspace.fotoWorkspace" :src="workspace.fotoWorkspace" :alt="workspace.nome" />
                <div v-else class="workspace-icon-placeholder">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                  </svg>
                </div>
              </div>
              <div class="user-select-info">
                <h4>{{ workspace.nome }}</h4>
                <p>{{ workspace.tipo }}</p>
              </div>
              <div v-if="isWorkspaceAssignedToUser(workspace.id)" class="badge-already-member">
                {{ t('permissoes.alreadyHasAccess') }}
              </div>
            </div>
          </div>

          <!-- Contador de seleção -->
          <div v-if="selectedWorkspacesToAdd.length > 0" class="selection-counter">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
            </svg>
            <span>{{ selectedWorkspacesToAdd.length }} {{ selectedWorkspacesToAdd.length === 1 ? t('permissoes.workspaceSelectedOne') : t('permissoes.workspaceSelectedMany') }}</span>
            <button @click="clearWorkspaceSelection" class="clear-selection-btn">
              {{ t('permissoes.clear') }}
            </button>
          </div>

          <!-- Info sobre permissão -->
          <div class="permission-info">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span>{{ t('permissoes.addMemberHint') }} <strong>{{ t('permissoes.addMemberRole') }}</strong> {{ t('permissoes.addMemberWorkspacesSuffix') }}</span>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="showAddWorkspaceModal = false" class="btn-secondary">
            {{ t('common.cancel') }}
          </button>
          <button 
            @click="addWorkspacesToUser" 
            class="btn-primary"
            :disabled="selectedWorkspacesToAdd.length === 0"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            {{ selectedWorkspacesToAdd.length > 0 ? t('permissoes.addWorkspacesCount', { count: selectedWorkspacesToAdd.length }) : t('permissoes.addWorkspaces') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading inicial -->
    <div v-else class="loading-container">
      <div class="spinner"></div>
      <p style="color: var(--text-secondary)">{{ t('permissoes.checkingPermissions') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useThemeStore } from '../stores/theme'
import apiClient from '../services/api'
import Swal from 'sweetalert2'
import WorkspaceRoleSelect from '../components/workspace/WorkspaceRoleSelect.vue'

const router = useRouter()
const { t } = useI18n()
const themeStore = useThemeStore()

// Logo
const logoTextImage = computed(() => {
  if (themeStore.isLight) {
    return new URL('../assets/logo_t_p.png', import.meta.url).href
  } else {
    return new URL('../assets/logo_t_w.png', import.meta.url).href
  }
})

// Estado
const isCheckingPermission = ref(true)
const hasPermission = ref(false)
const loading = ref(false)
const detailsLoading = ref(false)
const currentView = ref('by-workspace') // 'by-workspace', 'by-user' ou 'by-screens'

// Dados
const workspaces = ref([])
const usuarios = ref([])
const selectedWorkspace = ref(null)
const selectedUser = ref(null)
const workspaceUsuarios = ref([])
const userWorkspaces = ref([])
const allWorkspaceAcessos = ref([])

// Modais
const showInviteModal = ref(false)
const showAddWorkspaceModal = ref(false)
const searchUser = ref('')
const selectedUsersToInvite = ref([])
const searchWorkspace = ref('')
const selectedWorkspacesToAdd = ref([])

// Métodos auxiliares
const getInitials = (name) => {
  if (!name) return '?'
  const parts = name.split(' ')
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

const getUserWorkspaceCount = (usuarioId) => {
  return allWorkspaceAcessos.value.filter(a => a.usuarioId === usuarioId && a.ativo).length
}

const getWorkspaceUsers = (workspaceId) => {
  const usuarios = allWorkspaceAcessos.value.filter(a => 
    a.workspaceId === workspaceId && a.ativo && a.usuarioId
  )
  console.log(`👥 Workspace ${workspaceId}: ${usuarios.length} usuários encontrados`)
  return usuarios
}

// Filtrar usuários para convidar
const filteredUsuariosToInvite = computed(() => {
  if (!searchUser.value) return usuarios.value
  
  const search = searchUser.value.toLowerCase()
  return usuarios.value.filter(u => 
    u.nomeCompleto.toLowerCase().includes(search) ||
    u.email.toLowerCase().includes(search)
  )
})

// Verificar se usuário já está no workspace
const isUserInWorkspace = (usuarioId) => {
  return workspaceUsuarios.value.some(a => a.usuarioId === usuarioId)
}

// Verificar se usuário está selecionado
const isUserSelected = (usuarioId) => {
  return selectedUsersToInvite.value.some(u => u.id === usuarioId)
}

// Toggle seleção de usuário
const toggleUserSelection = (usuario) => {
  if (isUserInWorkspace(usuario.id)) return
  
  const index = selectedUsersToInvite.value.findIndex(u => u.id === usuario.id)
  if (index > -1) {
    selectedUsersToInvite.value.splice(index, 1)
  } else {
    selectedUsersToInvite.value.push(usuario)
  }
}

// Limpar seleção
const clearSelection = () => {
  selectedUsersToInvite.value = []
}

// === FUNÇÕES PARA MODAL DE ADICIONAR WORKSPACES AO USUÁRIO ===

// Filtrar workspaces disponíveis
const filteredWorkspacesToAdd = computed(() => {
  if (!searchWorkspace.value) return workspaces.value
  
  const search = searchWorkspace.value.toLowerCase()
  return workspaces.value.filter(w => 
    w.nome.toLowerCase().includes(search) ||
    w.tipo.toLowerCase().includes(search)
  )
})

// Verificar se workspace já está atribuído ao usuário
const isWorkspaceAssignedToUser = (workspaceId) => {
  return userWorkspaces.value.some(a => a.workspaceId === workspaceId)
}

// Verificar se workspace está selecionado
const isWorkspaceSelected = (workspaceId) => {
  return selectedWorkspacesToAdd.value.some(w => w.id === workspaceId)
}

// Toggle seleção de workspace
const toggleWorkspaceSelection = (workspace) => {
  if (isWorkspaceAssignedToUser(workspace.id)) return
  
  const index = selectedWorkspacesToAdd.value.findIndex(w => w.id === workspace.id)
  if (index > -1) {
    selectedWorkspacesToAdd.value.splice(index, 1)
  } else {
    selectedWorkspacesToAdd.value.push(workspace)
  }
}

// Limpar seleção de workspaces
const clearWorkspaceSelection = () => {
  selectedWorkspacesToAdd.value = []
}

// Adicionar múltiplos workspaces ao usuário
const addWorkspacesToUser = async () => {
  if (selectedWorkspacesToAdd.value.length === 0 || !selectedUser.value) return
  
  try {
    detailsLoading.value = true
    
    // Criar array de promises para adicionar todos os workspaces em paralelo
    const promises = selectedWorkspacesToAdd.value.map(workspace => 
      apiClient.post(`/workspace-acesso/workspace/${workspace.id}/usuarios`, {
        usuarioId: selectedUser.value.id,
        roleWorkspace: 'MEMBER'
      })
    )
    
    await Promise.all(promises)
    
    // Atualizar localmente os dados
    for (const workspace of selectedWorkspacesToAdd.value) {
      const newAcesso = {
        id: Date.now() + Math.random(), // ID temporário
        workspaceId: workspace.id,
        workspaceNome: workspace.nome,
        workspaceFotoWorkspace: workspace.fotoWorkspace,
        workspaceCorWorkspace: workspace.corWorkspace,
        workspaceTipo: workspace.tipo,
        workspaceIcone: workspace.icone,
        usuarioId: selectedUser.value.id,
        usuarioNome: selectedUser.value.nomeCompleto,
        usuarioEmail: selectedUser.value.email,
        usuarioFotoPerfil: selectedUser.value.fotoPerfil,
        roleWorkspace: 'MEMBER',
        ativo: true
      }
      
      userWorkspaces.value.push(newAcesso)
      allWorkspaceAcessos.value.push(newAcesso)
      
      // Atualizar o workspace com o novo usuário
      const ws = workspaces.value.find(w => w.id === workspace.id)
      if (ws) {
        ws.totalUsuarios = (ws.totalUsuarios || 0) + 1
      }
    }
    
    // Sucesso
    await Swal.fire({
      icon: 'success',
      title: t('permissoes.swal.successTitle'),
      text: t('permissoes.swal.workspacesAddedSuccess', {
        count: selectedWorkspacesToAdd.value.length,
        unit: selectedWorkspacesToAdd.value.length === 1 ? t('permissoes.swal.workspacesAddedOne') : t('permissoes.swal.workspacesAddedMany')
      }),
      timer: 2000,
      showConfirmButton: false
    })
    
    // Fechar modal e limpar seleção
    showAddWorkspaceModal.value = false
    selectedWorkspacesToAdd.value = []
    searchWorkspace.value = ''
    
  } catch (error) {
    console.error('Erro ao adicionar workspaces:', error)
    await Swal.fire({
      icon: 'error',
      title: t('permissoes.swal.errorTitle'),
      text: t('permissoes.swal.workspacesAddError'),
      confirmButtonText: t('common.confirm')
    })
  } finally {
    detailsLoading.value = false
  }
}

// Convidar múltiplos usuários para workspace
const inviteUsersToWorkspace = async () => {
  if (selectedUsersToInvite.value.length === 0 || !selectedWorkspace.value) return
  
  try {
    loading.value = true
    
    // Adicionar todos os usuários em paralelo
    const promises = selectedUsersToInvite.value.map(usuario =>
      apiClient.post(`/workspace-acesso/workspace/${selectedWorkspace.value.id}/usuarios`, {
        usuarioId: usuario.id,
        roleWorkspace: 'MEMBER'
      })
    )
    
    const responses = await Promise.all(promises)
    responses.forEach(response => {
      workspaceUsuarios.value.push(response.data)
    })
    
    // Atualizar contagem
    const index = workspaces.value.findIndex(w => w.id === selectedWorkspace.value.id)
    if (index !== -1) {
      workspaces.value[index].totalUsuarios = workspaceUsuarios.value.filter(a => a.ativo).length
    }
    
    // Limpar seleção e fechar modal
    selectedUsersToInvite.value = []
    searchUser.value = ''
    showInviteModal.value = false
    
    // Mostrar mensagem de sucesso
    await Swal.fire({
      title: t('permissoes.swal.successTitle'),
      text: t('permissoes.swal.usersAddedSuccess', {
        count: promises.length,
        unit: promises.length === 1 ? t('permissoes.swal.usersAddedOne') : t('permissoes.swal.usersAddedMany')
      }),
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    })
  } catch (error) {
    console.error('Erro ao convidar usuários:', error)
    await Swal.fire({
      title: t('permissoes.swal.errorTitle'),
      text: t('permissoes.swal.usersAddError'),
      icon: 'error'
    })
  } finally {
    loading.value = false
  }
}

// Verificar permissão para gerenciar permissões
// Pode gerenciar se:
// 1. É admin do sistema (role_id = 1)
// 2. É OWNER ou ADMIN de pelo menos 1 workspace
const checkAdminPermission = async () => {
  try {
    const response = await apiClient.get('/users/me/can-manage-permissions')
    hasPermission.value = response.data.canManage
  } catch (error) {
    console.error('Erro ao verificar permissão:', error)
    hasPermission.value = false
  } finally {
    isCheckingPermission.value = false
  }
}

// Carregar dados
// Endpoint específico para página de permissões:
// - Admin do sistema: TODOS os workspaces
// - Usuário normal: apenas workspaces onde é OWNER ou ADMIN
const loadWorkspaces = async () => {
  try {
    loading.value = true
    const response = await apiClient.get('/workspaces/for-permissions')
    workspaces.value = response.data
    console.log('✅ Workspaces carregados para permissões:', response.data.length)
  } catch (error) {
    console.error('Erro ao carregar workspaces:', error)
  } finally {
    loading.value = false
  }
}

// Carregar TODOS os usuários do sistema
// Agora qualquer pessoa com acesso à página pode ver todos os usuários
const loadUsuarios = async () => {
  try {
    loading.value = true
    const response = await apiClient.get('/users/all')
    usuarios.value = response.data
    console.log('✅ Usuários carregados:', response.data.length)
  } catch (error) {
    console.error('Erro ao carregar usuários:', error)
  } finally {
    loading.value = false
  }
}

const selectWorkspace = async (workspace) => {
  selectedWorkspace.value = workspace
  try {
    detailsLoading.value = true
    const response = await apiClient.get(`/workspace-acesso/workspace/${workspace.id}/usuarios`)
    workspaceUsuarios.value = response.data
    
    // Atualizar contagem de usuários do workspace
    const index = workspaces.value.findIndex(w => w.id === workspace.id)
    if (index !== -1) {
      workspaces.value[index].totalUsuarios = response.data.filter(a => a.ativo).length
    }
  } catch (error) {
    console.error('Erro ao carregar usuários do workspace:', error)
  } finally {
    detailsLoading.value = false
  }
}

const selectUser = async (usuario) => {
  selectedUser.value = usuario
  try {
    detailsLoading.value = true
    const response = await apiClient.get(`/workspace-acesso/usuario/${usuario.id}/workspaces`)
    userWorkspaces.value = response.data
  } catch (error) {
    console.error('Erro ao carregar workspaces do usuário:', error)
  } finally {
    detailsLoading.value = false
  }
}

const updateUserPermission = async (acesso) => {
  try {
    await apiClient.put(`/workspace-acesso/${acesso.id}/role`, {
      roleWorkspace: acesso.roleWorkspace
    })
    console.log('Permissão atualizada com sucesso')
  } catch (error) {
    console.error('Erro ao atualizar permissão:', error)
    alert(t('permissoes.swal.updatePermissionError'))
    // Recarregar dados
    if (selectedWorkspace.value) {
      selectWorkspace(selectedWorkspace.value)
    }
  }
}

const updateWorkspacePermission = async (acesso) => {
  try {
    await apiClient.put(`/workspace-acesso/${acesso.id}/role`, {
      roleWorkspace: acesso.roleWorkspace
    })
    console.log('Permissão atualizada com sucesso')
  } catch (error) {
    console.error('Erro ao atualizar permissão:', error)
    alert(t('permissoes.swal.updatePermissionError'))
    // Recarregar dados
    if (selectedUser.value) {
      selectUser(selectedUser.value)
    }
  }
}

const removeUserFromWorkspace = async (acesso) => {
  const result = await Swal.fire({
    title: t('permissoes.swal.confirmTitle'),
    html: t('permissoes.swal.confirmRemoveUser', { name: acesso.usuarioNome }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: t('permissoes.swal.confirmYes'),
    cancelButtonText: t('common.cancel')
  })
  
  if (!result.isConfirmed) return
  
  try {
    detailsLoading.value = true
    await apiClient.delete(`/workspace-acesso/${acesso.workspaceId}/usuarios/${acesso.usuarioId}`)
    
    // Atualizar dados localmente sem recarregar
    workspaceUsuarios.value = workspaceUsuarios.value.filter(u => u.id !== acesso.id)
    
    // Atualizar contagem
    const index = workspaces.value.findIndex(w => w.id === selectedWorkspace.value.id)
    if (index !== -1) {
      workspaces.value[index].totalUsuarios = workspaceUsuarios.value.filter(a => a.ativo).length
    }
    
    await Swal.fire({
      title: t('permissoes.swal.removedTitle'),
      text: t('permissoes.swal.userRemoved'),
      icon: 'success',
      timer: 1500,
      showConfirmButton: false
    })
  } catch (error) {
    console.error('Erro ao remover usuário:', error)
    await Swal.fire({
      title: t('permissoes.swal.errorTitle'),
      text: t('permissoes.swal.userRemoveError'),
      icon: 'error'
    })
  } finally {
    detailsLoading.value = false
  }
}

const removeWorkspaceFromUser = async (acesso) => {
  const result = await Swal.fire({
    title: t('permissoes.swal.confirmTitle'),
    html: t('permissoes.swal.confirmRemoveAccess', { user: selectedUser.value.nomeCompleto, workspace: acesso.workspaceNome }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: t('permissoes.swal.confirmYes'),
    cancelButtonText: t('common.cancel')
  })
  
  if (!result.isConfirmed) return
  
  try {
    detailsLoading.value = true
    await apiClient.delete(`/workspace-acesso/${acesso.workspaceId}/usuarios/${selectedUser.value.id}`)
    
    // Atualizar dados localmente sem recarregar
    userWorkspaces.value = userWorkspaces.value.filter(w => w.id !== acesso.id)
    
    await Swal.fire({
      title: t('permissoes.swal.removedTitle'),
      text: t('permissoes.swal.accessRemoved'),
      icon: 'success',
      timer: 1500,
      showConfirmButton: false
    })
  } catch (error) {
    console.error('Erro ao remover acesso:', error)
    await Swal.fire({
      title: t('permissoes.swal.errorTitle'),
      text: t('permissoes.swal.accessRemoveError'),
      icon: 'error'
    })
  } finally {
    detailsLoading.value = false
  }
}

// === FUNCIONALIDADE: PERMISSÕES POR TELA ===
const selectedScreenUser = ref(null)
const screenPermissions = ref({})
const originalScreenPermissions = ref({})
const loadingScreenPermissions = ref(false)
const savingScreenPermissions = ref(false)

const screenDefs = [
  { id: 'system-dashboard', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', color: '#3B82F6' },
  { id: 'workspaces', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', color: '#8B5CF6' },
  { id: 'workspace-permissoes', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', color: '#10B981' },
  { id: 'agenda', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', color: '#F59E0B' },
  { id: 'historia', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', color: '#EC4899' },
  { id: 'relatorios', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', color: '#6366F1' },
  { id: 'financeiro', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', color: '#14B8A6' }
]

const screensList = computed(() => screenDefs.map((screen) => ({
  ...screen,
  label: t(`permissoes.screens.${screen.id}.label`),
  description: t(`permissoes.screens.${screen.id}.description`)
})))

const buildDefaultScreenPermissions = () => {
  const defaults = {}
  screensList.value.forEach(s => {
    defaults[s.id] = s.id === 'system-dashboard'
  })
  return defaults
}

const isSelectedUserAdmin = computed(() => {
  if (!selectedScreenUser.value?.role) return false
  const roleName = selectedScreenUser.value.role.nome
  return roleName === 'ADMIN' || roleName === 'SUPER_ADMIN' || selectedScreenUser.value.role.id === 1
})

const screenPermissionsChanged = computed(() => {
  return JSON.stringify(screenPermissions.value) !== JSON.stringify(originalScreenPermissions.value)
})

const getScreenAccess = (screenId) => {
  if (isSelectedUserAdmin.value) return true
  return screenPermissions.value[screenId] !== false
}

const toggleScreenAccess = (screenId) => {
  if (isSelectedUserAdmin.value) return
  screenPermissions.value[screenId] = !getScreenAccess(screenId)
}

const selectUserForScreens = async (usuario) => {
  selectedScreenUser.value = usuario
  loadingScreenPermissions.value = true
  try {
    const response = await apiClient.get(`/permissoes-tela/usuario/${usuario.id}`)
    const perms = buildDefaultScreenPermissions()
    if (response.data && response.data.length > 0) {
      response.data.forEach(p => {
        perms[p.tela] = p.temAcesso
      })
    }
    screenPermissions.value = { ...perms }
    originalScreenPermissions.value = { ...perms }
  } catch (error) {
    console.error('Erro ao carregar permissões de tela:', error)
    const perms = buildDefaultScreenPermissions()
    screenPermissions.value = { ...perms }
    originalScreenPermissions.value = { ...perms }
  } finally {
    loadingScreenPermissions.value = false
  }
}

const saveUserScreenPermissions = async () => {
  if (!selectedScreenUser.value || isSelectedUserAdmin.value) return
  savingScreenPermissions.value = true
  try {
    const permissoes = screensList.value.map(s => ({
      tela: s.id,
      temAcesso: screenPermissions.value[s.id] !== false
    }))
    await apiClient.put(`/permissoes-tela/usuario/${selectedScreenUser.value.id}`, {
      usuarioId: selectedScreenUser.value.id,
      permissoes
    })
    originalScreenPermissions.value = { ...screenPermissions.value }
    await Swal.fire({
      icon: 'success',
      title: t('permissoes.swal.savedTitle'),
      text: t('permissoes.swal.permissionsSaved', { name: selectedScreenUser.value.nomeCompleto }),
      timer: 2000,
      showConfirmButton: false
    })
  } catch (error) {
    console.error('Erro ao salvar permissões de tela:', error)
    await Swal.fire({
      icon: 'error',
      title: t('permissoes.swal.errorTitle'),
      text: t('permissoes.swal.permissionsSaveError')
    })
  } finally {
    savingScreenPermissions.value = false
  }
}

// Inicialização
onMounted(async () => {
  await checkAdminPermission()
  
  if (hasPermission.value) {
    await Promise.all([loadWorkspaces(), loadUsuarios()])
    
    // Carregar todos os acessos para contar workspaces por usuário
    try {
      const workspacePromises = workspaces.value.map(w => 
        apiClient.get(`/workspace-acesso/workspace/${w.id}/usuarios`)
      )
      const results = await Promise.all(workspacePromises)
      allWorkspaceAcessos.value = results.flatMap(r => r.data)
      
      console.log('✅ Total de acessos carregados:', allWorkspaceAcessos.value.length)
      if (allWorkspaceAcessos.value.length > 0) {
        console.log('📋 Exemplo de acesso completo:', JSON.parse(JSON.stringify(allWorkspaceAcessos.value[0])))
      }
      console.log('📊 Total de results:', results.length)
      
      // Atualizar contagem de usuários nos workspaces
      workspaces.value = workspaces.value.map((w, index) => {
        // Usar o resultado correspondente ao index
        const acessosDoWorkspace = results[index].data
        const count = acessosDoWorkspace.filter(a => a.ativo).length
        
        console.log(`📁 Workspace ${w.nome} (ID: ${w.id}):`, count, 'usuários ativos')
        if (acessosDoWorkspace.length > 0 && count > 0) {
          console.log('  └─ Usuários:', acessosDoWorkspace.filter(a => a.ativo).map(a => a.usuarioNome || 'Sem nome'))
        }
        
        return {
          ...w,
          totalUsuarios: count
        }
      })
    } catch (error) {
      console.error('Erro ao carregar acessos:', error)
    }
  }
})
</script>

<style scoped>
/* Transições suaves globais */
* {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

/* Page Layout */
.workspace-permissoes-page {
  width: 100%;
  min-height: calc(100vh - 64px);
  background-color: var(--bg-primary);
  margin-left: 0;
}

/* Header Moderno */
.modern-header {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-primary);
  padding: 24px 0;
  margin-bottom: 32px;
}

.header-container {
  width: 100%;
  padding: 0 24px;
}

.breadcrumb-section {
  margin-bottom: 1rem;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--text-secondary);
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
}

.breadcrumb-item:hover {
  color: var(--info);
  background: var(--bg-tertiary);
}

.breadcrumb-separator {
  color: var(--text-tertiary);
}

.breadcrumb-current {
  color: var(--text-primary);
  font-weight: 500;
}

.title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--info), var(--info-dark));
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.title-text h1 {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.title-text .subtitle {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
}

.workspace-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
}

.workspace-badge.type-dashboard {
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(139, 92, 246, 0.1));
  color: var(--info);
  border: 1px solid rgba(124, 58, 237, 0.2);
}

.badge-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.actions-section {
  display: flex;
  gap: 0.75rem;
}

.view-toggles {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--bg-secondary);
  padding: 4px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.toggle-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.toggle-btn.active {
  background: var(--primary);
  color: white;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

/* Tooltip */
.tooltip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: var(--bg-primary);
  color: var(--text-primary);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  border: 1px solid var(--border-primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 1000;
  margin-top: 8px;
}

.tooltip::after {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-bottom-color: var(--bg-primary);
}

.tooltip-container:hover .tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(2px);
}

.action-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.action-button.secondary {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.action-button.secondary:hover {
  background: var(--bg-quaternary);
  border-color: var(--border-secondary);
}

/* Main Content */
.main-content {
  width: 100%;
  padding: 0;
  margin-left: 0;
}

/* No Permission Message */
.no-permission-message {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 70vh;
  padding: 2rem;
}

.no-permission-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  padding: 3rem;
  max-width: 500px;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}

.permission-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  color: var(--danger);
}

.no-permission-card h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 1rem;
}

.no-permission-card p {
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 2rem;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1rem;
  min-height: 50vh;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: var(--info);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* View Content */
.view-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 0 24px 24px 24px;
  height: calc(100vh - 250px);
  overflow: hidden;
}

.column-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  overflow: hidden;
  min-width: 0;
}

.column-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--border-primary);
  flex-shrink: 0;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  flex: 1;
}

/* Workspaces Grid */
.workspaces-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 28px 5px;
  flex: 1;
  overflow-y: auto;
  padding: 5px 0.5rem 1rem 0;
  align-content: start;
  width: 100%;
  grid-auto-rows: minmax(200px, auto);
}

.users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 28px 5px;
  flex: 1;
  overflow-y: auto;
  padding: 5px 0.5rem 1rem 0;
  align-content: start;
  width: 100%;
  grid-auto-rows: minmax(240px, auto);
}

/* Workspace Card - Design Moderno */
.workspace-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(0);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 220px;
  margin-bottom: 0.5rem;
}

.workspace-card:hover {
  border-color: var(--info);
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
}

.workspace-card.selected {
  border-color: var(--info);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.25);
  transform: translateY(-2px);
}

/* Workspace Cover/Thumbnail */
.workspace-cover {
  width: 100%;
  height: 80px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
}

.workspace-cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.workspace-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0.8;
}

/* Workspace Body */
.workspace-body {
  padding: 0.875rem 1rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  background: var(--bg-secondary);
}

.workspace-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.workspace-icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.workspace-info {
  flex: 1;
  min-width: 0;
}

.workspace-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.375rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.workspace-type-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Workspace Footer */
.workspace-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 0.625rem;
  margin-top: auto;
  border-top: 1px solid var(--border-primary);
}

.workspace-users-section {
  flex: 1;
  min-width: 0;
}

.workspace-users-avatars {
  display: flex;
  align-items: center;
}

.workspace-stat-badge {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  background: var(--bg-tertiary);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 0.8125rem;
  font-weight: 600;
  flex-shrink: 0;
}

.workspace-stat-badge svg {
  flex-shrink: 0;
}

.workspace-stat-badge span {
  line-height: 1;
}

/* User Card - Design Moderno */
.user-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(0);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 240px;
  margin-bottom: 0.5rem;
}

.user-card:hover {
  border-color: var(--info);
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
}

.user-card.selected {
  border-color: var(--info);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.25);
  transform: translateY(-2px);
}

/* User Card Header */
.user-card-header {
  padding: 1.5rem 1rem 1rem 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
}

.user-avatar-large {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid var(--bg-primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.user-avatar-large img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder-large {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

/* User Card Body */
.user-card-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  flex: 1;
  background: var(--bg-secondary);
}

.user-card-info {
  text-align: center;
}

.user-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.375rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* User Card Footer */
.user-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding-top: 0.625rem;
  margin-top: auto;
  border-top: 1px solid var(--border-primary);
}

.user-role-badge {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.user-role-badge svg {
  flex-shrink: 0;
}

.user-role-badge.role-admin,
.user-role-badge.role-super_admin {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.user-role-badge.role-user {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
}

.user-workspaces-count {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  background: var(--bg-tertiary);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 0.8125rem;
  font-weight: 600;
  flex-shrink: 0;
}

.user-workspaces-count svg {
  flex-shrink: 0;
}

.user-workspaces-count span {
  line-height: 1;
}

/* User Card Stats */
.user-card-stats {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Avatares Mini - Modernos */
.user-avatar-mini {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid var(--bg-primary);
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.user-avatar-mini:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 100 !important;
}

.user-avatar-mini img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder-mini {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
}

.user-avatar-mini.more-users {
  background: linear-gradient(135deg, var(--bg-tertiary), var(--bg-secondary));
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 700;
  border: 2px solid var(--bg-primary);
}

.stat {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

/* User Card */
.user-avatar-large {
  width: 64px;
  height: 64px;
  margin-bottom: 1rem;
}

.user-avatar-large img,
.user-avatar-large .avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  object-fit: cover;
}

.user-avatar-large .avatar-placeholder {
  background: linear-gradient(135deg, var(--info), var(--info-dark));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.5rem;
}

.user-card-info h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
}

.user-email {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0 0 0.5rem 0;
}

.user-role-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.user-role-badge.role-super_admin {
  background: linear-gradient(135deg, #9333ea, #7c3aed);
  color: white;
}

.user-role-badge.role-admin {
  background: linear-gradient(135deg, var(--info), var(--info-dark));
  color: white;
}

.user-role-badge.role-user {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
}

/* Details */
.workspace-details,
.user-workspaces-details {
  flex: 1;
  overflow-y: auto;
  padding-right: 0.5rem;
}

.user-workspaces-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
  padding: 5px 0 1rem 0;
}

/* Workspace Card Small - Para lista de usuário */
.workspace-card-small {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 160px;
}

.workspace-card-small:hover {
  border-color: var(--info);
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.workspace-small-cover {
  width: 100%;
  height: 60px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.workspace-small-cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.workspace-small-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0.8;
}

.workspace-small-body {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  flex: 1;
}

.workspace-small-header {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.workspace-icon-badge-small {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.workspace-small-info {
  flex: 1;
  min-width: 0;
}

.workspace-small-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.workspace-small-type {
  display: inline-block;
  padding: 0.125rem 0.375rem;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.workspace-small-footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-primary);
}

.role-select-small {
  flex: 1;
  padding: 0.375rem 0.5rem;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.role-select-small:hover:not(:disabled) {
  border-color: var(--border-secondary);
}

.role-select-small:focus {
  outline: none;
  border-color: var(--info);
}

.role-select-small:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-delete-small {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-delete-small:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: #ef4444;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

/* Users List */
.users-list,
.workspaces-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.user-item,
.workspace-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  transition: all 0.2s;
}

.user-item:hover,
.workspace-item:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.user-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.user-avatar img,
.user-avatar .avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
}

.user-avatar .avatar-placeholder {
  background: linear-gradient(135deg, var(--info), var(--info-dark));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
}

.user-info,
.workspace-item-info {
  flex: 1;
}

.user-info h4,
.workspace-item-info h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
}

.user-info p,
.workspace-item-info p {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin: 0;
}

.user-role,
.workspace-role {
  flex-shrink: 0;
}

.perm-owner-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.12) 100%);
  border: 1px solid rgba(245, 158, 11, 0.4);
  color: #b45309;
}

.perm-owner-badge--small {
  font-size: 11px;
  padding: 4px 8px;
}

.role-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.role-select:hover:not(:disabled) {
  border-color: var(--border-secondary);
}

.role-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-danger-icon {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--danger);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-danger-icon:hover {
  background: rgba(239, 68, 68, 0.1);
}

.workspace-icon-small {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

/* Buttons */
.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: var(--info);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background: var(--info-dark);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

/* Scrollbar */
.workspaces-grid::-webkit-scrollbar,
.users-grid::-webkit-scrollbar,
.workspace-details::-webkit-scrollbar,
.user-workspaces-details::-webkit-scrollbar {
  width: 6px;
}

.workspaces-grid::-webkit-scrollbar-track,
.users-grid::-webkit-scrollbar-track,
.workspace-details::-webkit-scrollbar-track,
.user-workspaces-details::-webkit-scrollbar-track {
  background: transparent;
}

.workspaces-grid::-webkit-scrollbar-thumb,
.users-grid::-webkit-scrollbar-thumb,
.workspace-details::-webkit-scrollbar-thumb,
.user-workspaces-details::-webkit-scrollbar-thumb {
  background: var(--border-primary);
  border-radius: 3px;
}

.workspaces-grid::-webkit-scrollbar-thumb:hover,
.users-grid::-webkit-scrollbar-thumb:hover,
.workspace-details::-webkit-scrollbar-thumb:hover,
.user-workspaces-details::-webkit-scrollbar-thumb:hover {
  background: var(--border-secondary);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-primary);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.search-box {
  position: relative;
  margin-bottom: 1rem;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: var(--text-tertiary);
}

.search-input {
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.5rem;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.9375rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--info);
  background: var(--bg-primary);
}

.users-select-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
}

.user-select-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid var(--border-primary);
}

.user-select-item:last-child {
  border-bottom: none;
}

.user-select-item:hover:not(.disabled) {
  background: var(--bg-tertiary);
}

.user-select-item.selected {
  background: rgba(59, 130, 246, 0.1);
  border-left: 3px solid var(--info);
}

.user-select-item.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.selection-checkbox {
  flex-shrink: 0;
}

.checkbox-checked,
.checkbox-unchecked {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.checkbox-checked {
  background: var(--info);
  color: white;
}

.checkbox-unchecked {
  border: 2px solid var(--border-primary);
  background: var(--bg-primary);
}

.user-select-item:hover:not(.disabled) .checkbox-unchecked {
  border-color: var(--info);
}

.user-select-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.user-select-avatar img,
.user-select-avatar .avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
}

.workspace-select-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.workspace-select-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.workspace-icon-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.user-select-info {
  flex: 1;
  min-width: 0;
}

.user-select-info h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-select-info p {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.badge-already-member {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  background: var(--bg-tertiary);
  color: var(--text-tertiary);
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.selection-counter {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  margin-top: 1rem;
}

.selection-counter svg {
  color: var(--info);
  flex-shrink: 0;
}

.selection-counter span {
  flex: 1;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-primary);
}

.clear-selection-btn {
  padding: 0.375rem 0.875rem;
  background: var(--bg-primary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-selection-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
  color: var(--text-primary);
}

.permission-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  margin-top: 1rem;
}

.permission-info svg {
  color: var(--info);
  flex-shrink: 0;
}

.permission-info span {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.permission-info strong {
  color: var(--text-primary);
  font-weight: 600;
}

.role-selection {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
}

.role-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.role-select-large {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.role-select-large:hover {
  border-color: var(--border-secondary);
}

.role-select-large:focus {
  outline: none;
  border-color: var(--info);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid var(--border-primary);
}

.btn-secondary {
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: var(--bg-tertiary);
}

/* === ESTILOS: PERMISSÕES POR TELA === */
.screen-permissions-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.admin-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.04));
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  color: var(--text-primary);
}

.admin-notice svg {
  color: #10B981;
  flex-shrink: 0;
  margin-top: 2px;
}

.admin-notice strong {
  display: block;
  margin-bottom: 4px;
  font-size: 0.875rem;
}

.admin-notice p {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.screens-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.screen-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  transition: all 0.2s ease;
}

.screen-item:hover {
  border-color: var(--info);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.screen-item.screen-disabled {
  opacity: 0.65;
  pointer-events: none;
}

.screen-info {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.screen-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
}

.screen-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.screen-name {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
}

.screen-description {
  margin: 0;
  font-size: 0.775rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.screen-toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.screen-toggle {
  position: relative;
  width: 44px;
  height: 24px;
  cursor: pointer;
}

.screen-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e5e7eb;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.screen-toggle input:checked + .toggle-slider {
  background-color: #10B981;
}

.screen-toggle input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.screen-toggle.toggle-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-label {
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 65px;
  text-align: center;
}

.toggle-label.access-granted {
  color: #10B981;
}

.toggle-label.access-denied {
  color: #EF4444;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Tema escuro */
body:not(.tema_clara) .toggle-slider {
  background-color: #374151;
}

body:not(.tema_clara) .screen-toggle input:checked + .toggle-slider {
  background-color: #10B981;
}

/* Responsive */
@media (max-width: 1200px) {
  .view-content {
    grid-template-columns: 1fr;
  }
  
  .workspaces-grid,
  .users-grid,
  .workspace-details,
  .user-workspaces-details {
    flex: 1;
  }
  
  .modal-content {
    width: 95%;
    max-height: 90vh;
  }

  .screen-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .screen-toggle-wrapper {
    align-self: flex-end;
  }
}
</style>
